import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useDispatch, useSelector } from 'react-redux';
import { Spin } from "antd";
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import { SignUp, SendVerificationEmail, setField } from "../../../slices/auth"
import { storeToken } from "../../profile/component/utils";
import { STATES } from "../../../constants/Countries";
import Select from 'react-select';
const UserInfoStep = ({ onNext, goToStep }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { signUpLoading, sendVerificationEmailLoading, currentUser } = useSelector(state => state?.auth || {});
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "US",
    password: "",
    confirmPassword: "",
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [hasCompleted, setHasCompleted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const warehouseReferral = searchParams.get('warehouseReferral');

  // Check if this step was already completed in another tab
  useEffect(() => {
    const checkCompletion = () => {
      const completedData = localStorage.getItem("userInfoCompleted");

      if (completedData && !hasCompleted) {
        try {
          const parsedData = JSON.parse(completedData);
          setHasCompleted(true);
          localStorage.removeItem("userInfoCompleted");
          onNext({ userInfo: parsedData.userInfo });
        } catch (error) {
          console.error("Error parsing completion data:", error);
        }
      }
    };

    checkCompletion();
    window.addEventListener("storage", checkCompletion);

    return () => {
      window.removeEventListener("storage", checkCompletion);
    };
  }, [hasCompleted, onNext]);

  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    const invitedEmail = searchParams.get('invitedEmail');
    if (invitedEmail) {
      const decodedEmail = decodeURIComponent(invitedEmail);
      setFormData((prev) => ({ ...prev, email: decodedEmail }));
    }

    if (token && email) {
      onNext({ userInfo: { email } });
    }
  }, [searchParams, onNext]);

  // 👇 single source of truth
  const runValidation = (field, value, allValues = formData) => {
    let error = "";

    switch (field) {
      case "email":
        if (!value) error = "Email is required";
        else if (!/^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) error = "Email is invalid";
        break;

      case "password":
        if (!value) error = "Password is required";
        else {
          const passwordRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=\[{\]};:'",.<>/?\\|`~]).{12,}$/;
          if (!passwordRegex.test(value)) {
            error =
              "Password must contain at least one lowercase letter, one uppercase letter, one number, one special character, and be at least 12 characters long";
          }
        }
        break;

      case "confirmPassword":
        if (!value) error = "Please confirm your password";
        else if (value !== allValues.password)
          error = "Passwords do not match";
        break;

      case "agreeToTerms":
        if (!value) error = "You must agree to the terms";
        break;

      case "phone":
        if (!value) {
          error = "Phone number is required";
        } else if (!/^[0-9+\s()-]*$/.test(value) || (value.replace(/\D/g, '').length < 7)) {
          error = "Please enter a valid phone number";
        }
        break;

      default:
        if (!value) {
          const formattedField = formatFieldName(field);
          error = `${formattedField} is required`;
        }
    }

    return error;
  };

  const validateField = (name, value, allValues = formData) => {
    const error = runValidation(name, value, allValues);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) newErrors[name] = error;
      else delete newErrors[name];
      return newErrors;
    });
    return !error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value, { ...formData, [name]: value });
  };

  const formatFieldName = (field) => {
    return field
      .replace(/([A-Z])/g, " $1")   
      .replace(/^./, (str) => str.toUpperCase());
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      const error = runValidation(field, formData[field], formData);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return; 

    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    if (params.has("token")) {
      params.delete("token");
      url.search = params.toString();
      window.history.replaceState({}, "", url.toString());
    }

    if (signUpLoading) return;

    try {
      dispatch(setField({
        module: 'user',
        field: 'email',
        value: formData.email
      }));
      const response = await dispatch(SignUp({ ...formData, warehouseReferral })).unwrap();
      if (!warehouseReferral){
        await dispatch(SendVerificationEmail({ 
          user: {
            email: formData.email 
          },
          type: 'sign-in'
        })).unwrap();
      }

      // Remove token from URL after successful signup
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);

      if (params.has("token")) {
        params.delete("token");
        url.search = params.toString();
        window.history.replaceState({}, "", url.toString());
      }

      // Set localStorage to sync with other tabs
      const completionData = {
        timestamp: Date.now().toString(),
        userInfo: formData
      };
      localStorage.setItem("userInfoCompleted", JSON.stringify(completionData));

      navigate(location.pathname, { replace: true });

      if (warehouseReferral) {
        const { jwt, current_user } = response?.data || {};
        if (jwt && current_user) {
          const verificationData = {
            timestamp: Date.now().toString(),
            token: jwt,
            email: formData.email,
            verified: true
          };
          localStorage.setItem("emailVerified", JSON.stringify(verificationData));
          storeToken(jwt, navigate, dispatch);
          navigate(location.pathname, { replace: true });
          goToStep && goToStep(3);
        }
      } else {
        onNext({ userInfo: formData });
      }
    } catch (err) {
      console.error("Signup failed:", err);
      setErrors(prev => ({
        ...prev,
        apiError: err?.message || "Signup failed, please try again"
      }));
    }
  };

  return (
    <div className="card">
      <div className="card-body animate-slide-in-right">
        <h2 className="card-title">
          Create Your Account
        </h2>
        <p className="description">
          Tell us about yourself and your business to get started with MarketOS.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="section-title">
              Personal Information
            </h3>
            <div className="grid">
              <div>
                <label className="form-label">
                  Full Name *
                </label>
                <div className="relative">
                  <input
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-input ${errors.name ? "error" : ""}`}
                    placeholder="John Doe"
                  />
                  {errors.name && <p className="error-message">{errors.name}</p>}
                </div>
              </div>
              <div>
                <label className="form-label">
                  Email Address *
                </label>
                <div className="relative">
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input ${errors.email ? "error" : ""}`}
                    placeholder="john@company.com"
                  />
                  {errors.email && <p className="error-message">{errors.email}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div>
            <h3 className="section-title">
              Company Information
            </h3>
            <div className="grid">
              <div>
                <label className="form-label">
                  Company Name *
                </label>
                <div className="relative">
                  <input
                    name="companyName"
                    type="text"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`form-input ${errors.companyName ? "error" : ""}`}
                    placeholder="Acme Corp"
                  />
                </div>
                {errors.companyName && <p className="error-message">{errors.companyName}</p>}
              </div>
              <div>
                <label className="form-label">
                  Phone Number *
                </label>
                <div className="relative">
                  <input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-input ${errors.phone ? "error" : ""}`}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                {errors.phone && <p className="error-message">{errors.phone}</p>}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div>
            <h3 className="section-title">
              Company Address
            </h3>
            <div className="space-y-4">
              <div>
                <label className="form-label">
                  Street Address *
                </label>
                <input
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleChange}
                  className={`form-input ${errors.address ? "error" : ""}`}
                  placeholder="123 Business St"
                />
                {errors.address && <p className="error-message">{errors.address}</p>}
              </div>
              
              <div className="grid-address-row">
                <div>
                  <label className="form-label">
                    City *
                  </label>
                  <input
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleChange}
                    className={`form-input ${errors.city ? "error" : ""}`}
                    placeholder="New York"
                  />
                  {errors.city && <p className="error-message">{errors.city}</p>}
                </div>
                <div>
                  <label className="form-label">
                    State *
                  </label>
                  <div className="react-select-container">
                    <Select
                      name="state"
                      placeholder="NY"
                      options={STATES}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      value={STATES.find(option => option.value === formData.state) || null}
                      onChange={(selectedOption) => {
                        handleChange({
                          target: {
                            name: 'state',
                            value: selectedOption ? selectedOption.value : ''
                          }
                        });
                      }}
                      classNamePrefix="react-select"
                      className={errors.state ? 'react-select__control--error' : ''}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: '38px',
                          borderColor: errors.state ? 'var(--onboarding-destructive) !important' : 'var(--onboarding-border)',
                          '&:hover': {
                            borderColor: errors.state ? 'var(--onboarding-destructive) !important' : 'var(--onboarding-primary)',
                          },
                          boxShadow: 'none',
                        }),
                      }}
                    />
                  </div>
                  {errors.state && <p className="error-message">{errors.state}</p>}
                </div>
                <div>
                  <label className="form-label">
                    ZIP Code *
                  </label>
                  <input
                    name="zipCode"
                    type="number"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className={`form-input no-spin ${errors.zipCode ? "error" : ""}`}
                    placeholder="10001"
                    onWheel={(e) => e.target.blur()}
                  />
                  {errors.zipCode && <p className="error-message">{errors.zipCode}</p>}
                </div>
              </div>
              
              <div>
                <label className="form-label">
                  Country *
                </label>
                <input
                  name="country"
                  type="text"
                  value={'US'}
                  disabled={true}
                  // onChange={handleChange}
                  className={`form-input ${errors.country ? "error" : ""}`}
                  placeholder="United States"
                />
                {errors.country && <p className="error-message">{errors.country}</p>}
              </div>
            </div>
          </div>

          {/* Password Section */}
          <div>
            <h3 className="section-title">
              Security
            </h3>
            <div className="grid">
              <div>
                <label className="form-label">
                  Password *
                </label>
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    className={`form-input ${errors.password ? "error" : ""}`}
                    placeholder="••••••••"
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)} 
                    className="border-0 bg-transparent absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <Eye className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                </div>
                {errors.password && <p className="error-message">{errors.password}</p>}
              </div>

              <div>
                <label className="form-label">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`form-input ${errors.confirmPassword ? "error" : ""}`}
                    placeholder="••••••••"
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                    className="border-0 bg-transparent absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <Eye className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && <p className="error-message">{errors.confirmPassword}</p>}
              </div>
            </div>
          </div>

          {/* Terms and Privacy Agreement */}
          <div>
            <div className="terms-section">
              <input
                type="checkbox"
                id="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({ ...prev, agreeToTerms: checked }))
                    validateField("agreeToTerms", checked);
                  }
                }
                className="mt-0.5"
              />
              <div className="flex-1">
                <label htmlFor="agreeToTerms" className="terms-text">
                  I agree to the{" "}
                  <a className="link">Terms of Service</a>
                  {" "}and{" "}
                  <a className="link">Privacy Policy</a>
                </label>
              </div>
            </div>
            {errors.agreeToTerms && <p className="error-message absolute">{errors.agreeToTerms}</p>}
          </div>
          

          <div className="actions">
            <button
              type="submit"
              className="btn-primary font-semibold"
              disabled={signUpLoading || sendVerificationEmailLoading}
            >
              {signUpLoading || sendVerificationEmailLoading ? "Processing..." : "Next Step"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { UserInfoStep };
